import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import TableSortLabel from "@mui/material/TableSortLabel";

import {
    Add as AddIcon,
    Close as CloseIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Refresh as RefreshIcon,
    Save as SaveIcon,
    Search as SearchIcon,
    Visibility as VisibilityIcon,
    FilterAlt as FilterAltIcon,
} from "@mui/icons-material";

import PageContainer from "../../../components/container/PageContainer";
import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";

import {
    AddDatabaseMaster,
    DeleteDatabaseMaster,
    GetApplicationList,
    GetApplicationsModulesList,
    GetApplicationsSubModulesList,
    GetDatabaseMasterList,
    UpdateDatabaseMaster,
} from "../../../services/admin";

const DATABASE_OPTIONS = [
    "SSWhite_HR",
    "ITEIndia",
    "Surgical_Implant",
    "Pluto",
    "MasterSheet_PowerBi",
];

const SCHEMA_OPTIONS = [
    { value: "emp", label: "emp" },
    { value: "dbo", label: "dbo" },
    { value: "document", label: "document" },
    { value: "master", label: "master" },
    { value: "OTHER", label: "Other" },
];

const STANDARD_SCHEMAS = [
    "emp",
    "dbo",
    "document",
    "master",
];

const BCrumb = [
    { to: "/", title: "Admin" },
    { title: "Master Database Management" },
];

const createRowKey = (prefix) =>
    `${prefix}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;

const createEmptyTable = () => ({
    rowKey: createRowKey("table"),
    schemaOption: "",
    customSchemaName: "",
    tableName: "",
    tableDescription: "",
    isActive: true,
});

const createEmptyStoredProcedure = () => ({
    rowKey: createRowKey("sp"),
    schemaOption: "",
    customSchemaName: "",
    spName: "",
    spDescription: "",
    isActive: true,
});

const createEmptyColumnFilters = () => ({
    application: "",
    module: "",
    subModule: "",
    tables: "",
    storedProcedures: "",
});

const createEmptyForm = () => ({
    recordId: null,
    recordType: null,
    tableId: null,
    storedProcedureId: null,

    applicationId: "",
    moduleId: "",
    subModuleId: "",
    databaseName: "",

    tables: [createEmptyTable()],
    storedProcedures: [
        createEmptyStoredProcedure(),
    ],

    editSchemaOption: "",
    editCustomSchemaName: "",
    editName: "",
    editDescription: "",
    isActive: true,
});

const extractList = (response) => {
    const responseData = response?.data;

    if (Array.isArray(responseData)) {
        return responseData;
    }

    if (Array.isArray(responseData?.data)) {
        return responseData.data;
    }

    if (Array.isArray(responseData?.result)) {
        return responseData.result;
    }

    if (Array.isArray(responseData?.Result)) {
        return responseData.Result;
    }

    return [];
};

const extractSingleResult = (response) => {
    const list = extractList(response);

    if (list.length > 0) {
        return list[0];
    }

    const responseData = response?.data;

    if (
        responseData?.data &&
        !Array.isArray(responseData.data)
    ) {
        return responseData.data;
    }

    return responseData || {};
};

const getErrorMessage = (error) => {
    const responseData = error?.response?.data;

    if (typeof responseData === "string") {
        return responseData;
    }

    return (
        responseData?.message ||
        responseData?.Message ||
        responseData?.detail ||
        responseData?.title ||
        responseData?.error ||
        error?.message ||
        "Something went wrong. Please try again."
    );
};

const normalizeBoolean = (value) =>
    !(
        value === false ||
        value === 0 ||
        value === "0" ||
        value === "false"
    );

const toNullableNumber = (value) => {
    if (
        value === "" ||
        value === null ||
        value === undefined
    ) {
        return null;
    }

    const parsedValue = Number(value);

    return Number.isNaN(parsedValue)
        ? null
        : parsedValue;
};

const resolveSchemaName = (
    schemaOption,
    customSchemaName
) => {
    if (schemaOption === "OTHER") {
        return String(
            customSchemaName || ""
        ).trim();
    }

    return String(schemaOption || "").trim();
};

const getSchemaSelection = (schemaName) => {
    const originalSchema =
        String(schemaName || "").trim();

    const normalizedSchema =
        originalSchema.toLowerCase();

    const isStandard =
        STANDARD_SCHEMAS.includes(
            normalizedSchema
        );

    return {
        schemaOption: isStandard
            ? normalizedSchema
            : originalSchema
                ? "OTHER"
                : "",

        customSchemaName: isStandard
            ? ""
            : originalSchema,
    };
};

const isValidSchemaName = (schemaName) =>
    /^[A-Za-z_][A-Za-z0-9_]*$/.test(
        schemaName
    );

const normalizeApplication = (item) => ({
    id:
        item?.Application_ID ??
        item?.application_ID ??
        item?.ApplicationId ??
        item?.applicationId ??
        item?.Id ??
        item?.id ??
        "",

    name:
        item?.Application_Name ??
        item?.application_Name ??
        item?.ApplicationName ??
        item?.applicationName ??
        item?.Name ??
        item?.name ??
        "",
});

const normalizeModule = (item) => ({
    id:
        item?.Module_ID ??
        item?.module_ID ??
        item?.ModuleId ??
        item?.moduleId ??
        item?.Id ??
        item?.id ??
        "",

    applicationId:
        item?.Application_ID ??
        item?.application_ID ??
        item?.ApplicationId ??
        item?.applicationId ??
        "",

    name:
        item?.Module_Name ??
        item?.module_Name ??
        item?.ModuleName ??
        item?.moduleName ??
        item?.Name ??
        item?.name ??
        "",
});

const normalizeSubModule = (item) => ({
    id:
        item?.Sub_Module_ID ??
        item?.sub_Module_ID ??
        item?.SubModuleId ??
        item?.subModuleId ??
        item?.Id ??
        item?.id ??
        "",

    applicationId:
        item?.Application_ID ??
        item?.application_ID ??
        item?.ApplicationId ??
        item?.applicationId ??
        "",

    moduleId:
        item?.Module_ID ??
        item?.module_ID ??
        item?.ModuleId ??
        item?.moduleId ??
        "",

    name:
        item?.Sub_Module_Name ??
        item?.sub_Module_Name ??
        item?.SubModuleName ??
        item?.subModuleName ??
        item?.Name ??
        item?.name ??
        "",
});

const normalizeDatabaseRecord = (item) => {
    const tableId =
        item?.Table_ID ??
        item?.table_ID ??
        item?.tableId ??
        null;

    const storedProcedureId =
        item?.Stored_Procedure_ID ??
        item?.stored_Procedure_ID ??
        item?.storedProcedureId ??
        null;

    const recordType = String(
        item?.RecordType ??
        item?.recordType ??
        (tableId ? "TABLE" : "SP")
    ).toUpperCase();

    const recordId =
        item?.RecordId ??
        item?.recordId ??
        tableId ??
        storedProcedureId ??
        null;

    return {
        recordType,
        recordId,
        tableId,
        storedProcedureId,
        clientKey: `${recordType}-${recordId}`,

        applicationId:
            item?.Application_ID ??
            item?.application_ID ??
            item?.applicationId ??
            "",

        moduleId:
            item?.Module_ID ??
            item?.module_ID ??
            item?.moduleId ??
            "",

        subModuleId:
            item?.Sub_Module_ID ??
            item?.sub_Module_ID ??
            item?.subModuleId ??
            "",

        databaseName:
            item?.Database_Name ??
            item?.database_Name ??
            item?.databaseName ??
            "",

        schemaName:
            item?.Schema_Name ??
            item?.schema_Name ??
            item?.schemaName ??
            "",

        tableName:
            item?.Table_Name ??
            item?.table_Name ??
            item?.tableName ??
            "",

        tableDescription:
            item?.Table_Description ??
            item?.table_Description ??
            item?.tableDescription ??
            "",

        spName:
            item?.Stored_Procedure_Name ??
            item?.stored_Procedure_Name ??
            item?.storedProcedureName ??
            "",

        spDescription:
            item?.Stored_Procedure_Description ??
            item?.stored_Procedure_Description ??
            item?.storedProcedureDescription ??
            "",

        isActive: normalizeBoolean(
            item?.IsActive ??
            item?.isActive
        ),
    };
};

const MasterDatabase = () => {
    const [records, setRecords] =
        useState([]);

    const [applications, setApplications] =
        useState([]);

    const [modules, setModules] =
        useState([]);

    const [subModules, setSubModules] =
        useState([]);

    const [form, setForm] = useState(
        createEmptyForm
    );

    const [showForm, setShowForm] =
        useState(false);

    const [viewScope, setViewScope] =
        useState(null);

    const [
        scopeAddContext,
        setScopeAddContext,
    ] = useState(null);

    const [deleteRecord, setDeleteRecord] =
        useState(null);

    const [searchText, setSearchText] =
        useState("");

    const [
        recordTypeFilter,
        setRecordTypeFilter,
    ] = useState("");

    const [loading, setLoading] =
        useState(false);

    const [saving, setSaving] =
        useState(false);

    const [deleting, setDeleting] =
        useState(false);

    const [
        statusUpdatingKey,
        setStatusUpdatingKey,
    ] = useState("");

    const [page, setPage] = useState(0);

    const [rowsPerPage, setRowsPerPage] =
        useState(5);

    const [order, setOrder] =
        useState("asc");

    const [orderBy, setOrderBy] =
        useState("");

    const [
        showAllFilters,
        setShowAllFilters,
    ] = useState(false);

    const [
        columnFilters,
        setColumnFilters,
    ] = useState(
        createEmptyColumnFilters
    );

    const [
        notification,
        setNotification,
    ] = useState({
        open: false,
        severity: "success",
        message: "",
    });

    const isEditMode = Boolean(
        form.recordId
    );

    const isScopeAddMode = Boolean(
        scopeAddContext
    );

    const showNotification = useCallback(
        (message, severity = "success") => {
            setNotification({
                open: true,
                severity,
                message,
            });
        },
        []
    );

    const closeNotification = () => {
        setNotification((previous) => ({
            ...previous,
            open: false,
        }));
    };

    const loadLookups = useCallback(
        async () => {
            const [
                applicationResponse,
                moduleResponse,
                subModuleResponse,
            ] = await Promise.all([
                GetApplicationList(),
                GetApplicationsModulesList(),
                GetApplicationsSubModulesList(),
            ]);

            setApplications(
                extractList(applicationResponse)
                    .map(normalizeApplication)
                    .filter(
                        (item) =>
                            item.id &&
                            item.name
                    )
            );

            setModules(
                extractList(moduleResponse)
                    .map(normalizeModule)
                    .filter(
                        (item) =>
                            item.id &&
                            item.name
                    )
            );

            setSubModules(
                extractList(subModuleResponse)
                    .map(normalizeSubModule)
                    .filter(
                        (item) =>
                            item.id &&
                            item.name
                    )
            );
        },
        []
    );

    const loadRecords = useCallback(
        async () => {
            const response =
                await GetDatabaseMasterList();

            setRecords(
                extractList(response)
                    .map(normalizeDatabaseRecord)
                    .filter(
                        (item) => item.recordId
                    )
            );
        },
        []
    );

    const loadPageData = useCallback(
        async () => {
            try {
                setLoading(true);

                await Promise.all([
                    loadLookups(),
                    loadRecords(),
                ]);
            } catch (error) {
                showNotification(
                    getErrorMessage(error),
                    "error"
                );
            } finally {
                setLoading(false);
            }
        },
        [
            loadLookups,
            loadRecords,
            showNotification,
        ]
    );

    useEffect(() => {
        loadPageData();
    }, [loadPageData]);

    const filteredModules = useMemo(() => {
        if (!form.applicationId) {
            return [];
        }

        return modules.filter(
            (item) =>
                String(item.applicationId) ===
                String(form.applicationId)
        );
    }, [
        modules,
        form.applicationId,
    ]);

    const filteredSubModules =
        useMemo(() => {
            if (!form.moduleId) {
                return [];
            }

            return subModules.filter(
                (item) =>
                    String(item.moduleId) ===
                    String(form.moduleId)
            );
        }, [
            subModules,
            form.moduleId,
        ]);

    const getApplicationName =
        useCallback(
            (applicationId) =>
                applications.find(
                    (item) =>
                        String(item.id) ===
                        String(applicationId)
                )?.name || "-",
            [applications]
        );

    const getModuleName =
        useCallback(
            (moduleId) =>
                modules.find(
                    (item) =>
                        String(item.id) ===
                        String(moduleId)
                )?.name || "-",
            [modules]
        );

    const getSubModuleName =
        useCallback(
            (subModuleId) => {
                if (
                    subModuleId === null ||
                    subModuleId === undefined ||
                    subModuleId === ""
                ) {
                    return "Module Level";
                }

                return (
                    subModules.find(
                        (item) =>
                            String(item.id) ===
                            String(subModuleId)
                    )?.name || "-"
                );
            },
            [subModules]
        );

    const handleSort = (property) => {
        const isAscending =
            orderBy === property &&
            order === "asc";

        setOrder(
            isAscending ? "desc" : "asc"
        );

        setOrderBy(property);
    };

    const handleColumnFilterChange = (
        field,
        value
    ) => {
        setColumnFilters((previous) => ({
            ...previous,
            [field]: value,
        }));

        setPage(0);
    };

    const clearColumnFilter = (field) => {
        setColumnFilters((previous) => ({
            ...previous,
            [field]: "",
        }));

        setPage(0);
    };

    const handleResetFilters = () => {
        setSearchText("");
        setRecordTypeFilter("");
        setColumnFilters(
            createEmptyColumnFilters()
        );
        setShowAllFilters(false);
        setOrder("asc");
        setOrderBy("");
        setPage(0);
    };

    const renderSortableHeader = (
        label,
        field,
        align = "left"
    ) => (
        <TableCell
            align={align}
            sx={{ fontWeight: 600 }}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent={
                    align === "center"
                        ? "center"
                        : "flex-start"
                }
                gap={0.5}
                sx={{ color: "text.primary" }}
            >
                <TableSortLabel
                    active={orderBy === field}
                    direction={
                        orderBy === field
                            ? order
                            : "asc"
                    }
                    onClick={() =>
                        handleSort(field)
                    }
                >
                    {label}
                </TableSortLabel>
            </Box>
        </TableCell>
    );

    const renderColumnFilter = (
        field,
        placeholder = "Search...",
        align = "left"
    ) => (
        <TableCell align={align}>
            <TextField
                size="small"
                fullWidth
                value={
                    columnFilters[field] || ""
                }
                onChange={(event) =>
                    handleColumnFilterChange(
                        field,
                        event.target.value
                    )
                }
                placeholder={placeholder}
                InputProps={{
                    endAdornment:
                        columnFilters[field] ? (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        clearColumnFilter(field)
                                    }
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                }}
            />
        </TableCell>
    );

    const getRecordName = (record) =>
        record.recordType === "TABLE"
            ? record.tableName
            : record.spName;

    const getRecordDescription = (record) =>
        record.recordType === "TABLE"
            ? record.tableDescription
            : record.spDescription;

    const handleApplicationChange = (
        event
    ) => {
        setForm((previous) => ({
            ...previous,
            applicationId:
                event.target.value,
            moduleId: "",
            subModuleId: "",
        }));
    };

    const handleModuleChange = (event) => {
        setForm((previous) => ({
            ...previous,
            moduleId: event.target.value,
            subModuleId: "",
        }));
    };

    const handleFieldChange = (event) => {
        const { name, value } =
            event.target;

        setForm((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const handleTableChange = (
        rowKey,
        field,
        value
    ) => {
        setForm((previous) => ({
            ...previous,
            tables: previous.tables.map(
                (item) =>
                    item.rowKey === rowKey
                        ? {
                            ...item,
                            [field]: value,
                        }
                        : item
            ),
        }));
    };

    const handleTableSchemaChange = (
        rowKey,
        schemaOption
    ) => {
        setForm((previous) => ({
            ...previous,
            tables: previous.tables.map(
                (item) =>
                    item.rowKey === rowKey
                        ? {
                            ...item,
                            schemaOption,
                            customSchemaName:
                                schemaOption === "OTHER"
                                    ? item.customSchemaName
                                    : "",
                        }
                        : item
            ),
        }));
    };

    const addTableRow = () => {
        setForm((previous) => ({
            ...previous,
            tables: [
                ...previous.tables,
                createEmptyTable(),
            ],
        }));
    };

    const removeTableRow = (rowKey) => {
        setForm((previous) => {
            const remainingRows =
                previous.tables.filter(
                    (item) =>
                        item.rowKey !== rowKey
                );

            return {
                ...previous,
                tables:
                    remainingRows.length > 0
                        ? remainingRows
                        : [createEmptyTable()],
            };
        });
    };

    const handleStoredProcedureChange = (
        rowKey,
        field,
        value
    ) => {
        setForm((previous) => ({
            ...previous,
            storedProcedures:
                previous.storedProcedures.map(
                    (item) =>
                        item.rowKey === rowKey
                            ? {
                                ...item,
                                [field]: value,
                            }
                            : item
                ),
        }));
    };

    const handleStoredProcedureSchemaChange = (
        rowKey,
        schemaOption
    ) => {
        setForm((previous) => ({
            ...previous,
            storedProcedures:
                previous.storedProcedures.map(
                    (item) =>
                        item.rowKey === rowKey
                            ? {
                                ...item,
                                schemaOption,
                                customSchemaName:
                                    schemaOption ===
                                        "OTHER"
                                        ? item.customSchemaName
                                        : "",
                            }
                            : item
                ),
        }));
    };

    const addStoredProcedureRow = () => {
        setForm((previous) => ({
            ...previous,
            storedProcedures: [
                ...previous.storedProcedures,
                createEmptyStoredProcedure(),
            ],
        }));
    };

    const removeStoredProcedureRow = (
        rowKey
    ) => {
        setForm((previous) => {
            const remainingRows =
                previous.storedProcedures.filter(
                    (item) =>
                        item.rowKey !== rowKey
                );

            return {
                ...previous,
                storedProcedures:
                    remainingRows.length > 0
                        ? remainingRows
                        : [
                            createEmptyStoredProcedure(),
                        ],
            };
        });
    };

    const openAddForm = () => {
        setForm(createEmptyForm());
        setScopeAddContext(null);
        setViewScope(null);
        setShowForm(true);

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const resetForm = () => {
        if (saving) {
            return;
        }

        setForm(createEmptyForm());
        setScopeAddContext(null);
        setShowForm(false);
    };

    const openEditForm = (record) => {
        const schemaSelection =
            getSchemaSelection(
                record.schemaName
            );

        setViewScope(null);
        setScopeAddContext(null);
        setShowForm(true);

        setForm({
            ...createEmptyForm(),

            recordId: record.recordId,
            recordType: record.recordType,
            tableId: record.tableId,
            storedProcedureId:
                record.storedProcedureId,

            applicationId:
                record.applicationId,
            moduleId: record.moduleId,
            subModuleId:
                record.subModuleId ?? "",
            databaseName:
                record.databaseName || "",

            editSchemaOption:
                schemaSelection.schemaOption,

            editCustomSchemaName:
                schemaSelection.customSchemaName,

            editName:
                record.recordType === "TABLE"
                    ? record.tableName || ""
                    : record.spName || "",

            editDescription:
                record.recordType === "TABLE"
                    ? record.tableDescription || ""
                    : record.spDescription || "",

            isActive: normalizeBoolean(
                record.isActive
            ),
        });

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const getPreparedTableEntries = () =>
        form.tables
            .map((item) => ({
                ...item,

                schemaName:
                    resolveSchemaName(
                        item.schemaOption,
                        item.customSchemaName
                    ),

                tableName:
                    item.tableName.trim(),

                tableDescription:
                    item.tableDescription.trim(),

                isActive:
                    item.isActive !== false,
            }))
            .filter(
                (item) =>
                    item.schemaOption ||
                    item.customSchemaName.trim() ||
                    item.tableName ||
                    item.tableDescription
            );

    const getPreparedStoredProcedureEntries =
        () =>
            form.storedProcedures
                .map((item) => ({
                    ...item,

                    schemaName:
                        resolveSchemaName(
                            item.schemaOption,
                            item.customSchemaName
                        ),

                    spName:
                        item.spName.trim(),

                    spDescription:
                        item.spDescription.trim(),

                    isActive:
                        item.isActive !== false,
                }))
                .filter(
                    (item) =>
                        item.schemaOption ||
                        item.customSchemaName.trim() ||
                        item.spName ||
                        item.spDescription
                );

    const validateAddRows = (
        tableEntries,
        storedProcedureEntries
    ) => {
        if (
            tableEntries.length === 0 &&
            storedProcedureEntries.length === 0
        ) {
            return "Please enter at least one table or stored procedure.";
        }

        for (const item of tableEntries) {
            if (!item.schemaOption) {
                return "Please select a schema for every table row.";
            }

            if (!item.schemaName) {
                return "Please enter the custom schema name for every table row using Other.";
            }

            if (
                !isValidSchemaName(
                    item.schemaName
                )
            ) {
                return `Invalid table schema "${item.schemaName}". Use letters, numbers and underscores only, and do not start with a number.`;
            }

            if (!item.tableName) {
                return "Please enter the table name for every table row.";
            }

            if (
                item.tableName.includes(".")
            ) {
                return "Table name must not include schema. Select schema from the dropdown.";
            }

            if (!item.tableDescription) {
                return "Please enter the table description for every table row.";
            }
        }

        for (
            const item of
            storedProcedureEntries
        ) {
            if (!item.schemaOption) {
                return "Please select a schema for every stored procedure row.";
            }

            if (!item.schemaName) {
                return "Please enter the custom schema name for every stored procedure row using Other.";
            }

            if (
                !isValidSchemaName(
                    item.schemaName
                )
            ) {
                return `Invalid stored procedure schema "${item.schemaName}". Use letters, numbers and underscores only, and do not start with a number.`;
            }

            if (!item.spName) {
                return "Please enter the stored procedure name for every SP row.";
            }

            if (item.spName.includes(".")) {
                return "Stored procedure name must not include schema. Select schema from the dropdown.";
            }

            if (!item.spDescription) {
                return "Please enter the stored procedure description for every SP row.";
            }
        }

        const tableKeys =
            tableEntries.map(
                (item) =>
                    `${item.schemaName.toLowerCase()}|${item.tableName.toLowerCase()}`
            );

        if (
            new Set(tableKeys).size !==
            tableKeys.length
        ) {
            return "Duplicate table names in the same schema are not allowed.";
        }

        const spKeys =
            storedProcedureEntries.map(
                (item) =>
                    `${item.schemaName.toLowerCase()}|${item.spName.toLowerCase()}`
            );

        if (
            new Set(spKeys).size !==
            spKeys.length
        ) {
            return "Duplicate stored procedure names in the same schema are not allowed.";
        }

        return "";
    };

    const validateForm = (
        tableEntries,
        storedProcedureEntries
    ) => {
        if (!form.applicationId) {
            return "Please select an application.";
        }

        if (!form.moduleId) {
            return "Please select a module.";
        }

        if (!form.databaseName) {
            return "Please select a database.";
        }

        if (isEditMode) {
            const editSchemaName =
                resolveSchemaName(
                    form.editSchemaOption,
                    form.editCustomSchemaName
                );

            if (!form.editSchemaOption) {
                return "Please select a schema.";
            }

            if (!editSchemaName) {
                return "Please enter the custom schema name.";
            }

            if (
                !isValidSchemaName(
                    editSchemaName
                )
            ) {
                return "Schema may contain letters, numbers and underscores, and cannot start with a number.";
            }

            if (!form.editName.trim()) {
                return form.recordType ===
                    "TABLE"
                    ? "Please enter the table name."
                    : "Please enter the stored procedure name.";
            }

            if (
                form.editName
                    .trim()
                    .includes(".")
            ) {
                return "Object name must not include schema. Select schema separately.";
            }

            if (
                !form.editDescription.trim()
            ) {
                return form.recordType ===
                    "TABLE"
                    ? "Please enter the table description."
                    : "Please enter the stored procedure description.";
            }

            return "";
        }

        return validateAddRows(
            tableEntries,
            storedProcedureEntries
        );
    };

    const buildCommonPayload = () => ({
        Database_Name: form.databaseName,

        Application_ID: Number(
            form.applicationId
        ),

        Module_ID: Number(
            form.moduleId
        ),

        Sub_Module_ID:
            toNullableNumber(
                form.subModuleId
            ),
    });

    const handleSave = async () => {
        const tableEntries =
            getPreparedTableEntries();

        const storedProcedureEntries =
            getPreparedStoredProcedureEntries();

        const validationMessage =
            validateForm(
                tableEntries,
                storedProcedureEntries
            );

        if (validationMessage) {
            showNotification(
                validationMessage,
                "warning"
            );

            return;
        }

        try {
            setSaving(true);

            const commonPayload =
                buildCommonPayload();

            if (isEditMode) {
                const isTable =
                    form.recordType === "TABLE";

                const editSchemaName =
                    resolveSchemaName(
                        form.editSchemaOption,
                        form.editCustomSchemaName
                    );

                await UpdateDatabaseMaster({
                    ...commonPayload,

                    Schema_Name:
                        editSchemaName,

                    RecordType:
                        form.recordType,

                    Table_ID:
                        isTable
                            ? Number(form.tableId)
                            : null,

                    Table_Name:
                        isTable
                            ? form.editName.trim()
                            : null,

                    Table_Description:
                        isTable
                            ? form.editDescription.trim()
                            : null,

                    Stored_Procedure_ID:
                        !isTable
                            ? Number(
                                form.storedProcedureId
                            )
                            : null,

                    Stored_Procedure_Name:
                        !isTable
                            ? form.editName.trim()
                            : null,

                    Stored_Procedure_Description:
                        !isTable
                            ? form.editDescription.trim()
                            : null,

                    IsActive: Boolean(
                        form.isActive
                    ),
                });

                showNotification(
                    "Database record updated successfully."
                );

                setForm(createEmptyForm());
                setScopeAddContext(null);
                setShowForm(false);
                await loadRecords();

                return;
            }

            const requests = [
                ...tableEntries.map(
                    (item) =>
                        AddDatabaseMaster({
                            ...commonPayload,

                            Schema_Name:
                                item.schemaName,

                            RecordType: "TABLE",

                            Table_ID: null,

                            Table_Name:
                                item.tableName,

                            Table_Description:
                                item.tableDescription,

                            Stored_Procedure_ID: null,
                            Stored_Procedure_Name: null,
                            Stored_Procedure_Description:
                                null,

                            IsActive: Boolean(
                                item.isActive
                            ),
                        })
                ),

                ...storedProcedureEntries.map(
                    (item) =>
                        AddDatabaseMaster({
                            ...commonPayload,

                            Schema_Name:
                                item.schemaName,

                            RecordType: "SP",

                            Table_ID: null,
                            Table_Name: null,
                            Table_Description: null,

                            Stored_Procedure_ID: null,

                            Stored_Procedure_Name:
                                item.spName,

                            Stored_Procedure_Description:
                                item.spDescription,

                            IsActive: Boolean(
                                item.isActive
                            ),
                        })
                ),
            ];

            const results =
                await Promise.allSettled(
                    requests
                );

            const failedResults =
                results.filter(
                    (result) =>
                        result.status ===
                        "rejected"
                );

            const successCount =
                results.length -
                failedResults.length;

            await loadRecords();

            if (failedResults.length > 0) {
                showNotification(
                    `${successCount} record(s) saved and ${failedResults.length} failed. ${getErrorMessage(
                        failedResults[0].reason
                    )}`,
                    "warning"
                );

                return;
            }

            showNotification(
                `${successCount} database record(s) added successfully.`
            );

            setForm(createEmptyForm());
            setScopeAddContext(null);
            setShowForm(false);
        } catch (error) {
            showNotification(
                getErrorMessage(error),
                "error"
            );
        } finally {
            setSaving(false);
        }
    };

    const handleRecordStatusChange =
        async (record, checked) => {
            const isTable =
                record.recordType === "TABLE";

            try {
                setStatusUpdatingKey(
                    record.clientKey
                );

                await UpdateDatabaseMaster({
                    Database_Name:
                        record.databaseName,

                    Schema_Name:
                        record.schemaName,

                    Application_ID: Number(
                        record.applicationId
                    ),

                    Module_ID: Number(
                        record.moduleId
                    ),

                    Sub_Module_ID:
                        toNullableNumber(
                            record.subModuleId
                        ),

                    RecordType:
                        record.recordType,

                    Table_ID:
                        isTable
                            ? Number(record.tableId)
                            : null,

                    Table_Name:
                        isTable
                            ? record.tableName
                            : null,

                    Table_Description:
                        isTable
                            ? record.tableDescription
                            : null,

                    Stored_Procedure_ID:
                        !isTable
                            ? Number(
                                record.storedProcedureId
                            )
                            : null,

                    Stored_Procedure_Name:
                        !isTable
                            ? record.spName
                            : null,

                    Stored_Procedure_Description:
                        !isTable
                            ? record.spDescription
                            : null,

                    IsActive: checked,
                });

                setRecords((previous) =>
                    previous.map((item) =>
                        item.clientKey ===
                            record.clientKey
                            ? {
                                ...item,
                                isActive: checked,
                            }
                            : item
                    )
                );

                showNotification(
                    `${isTable
                        ? "Table"
                        : "Stored procedure"
                    } ${checked
                        ? "activated"
                        : "deactivated"
                    } successfully.`
                );
            } catch (error) {
                showNotification(
                    getErrorMessage(error),
                    "error"
                );
            } finally {
                setStatusUpdatingKey("");
            }
        };

    const openDeleteDialog = (record) => {
        setViewScope(null);
        setDeleteRecord(record);
    };

    const closeDeleteDialog = () => {
        if (!deleting) {
            setDeleteRecord(null);
        }
    };

    const handleDelete = async () => {
        if (!deleteRecord?.recordId) {
            showNotification(
                "Record ID is missing.",
                "error"
            );

            return;
        }

        try {
            setDeleting(true);

            const response =
                await DeleteDatabaseMaster({
                    tableId:
                        deleteRecord.recordType ===
                            "TABLE"
                            ? deleteRecord.tableId
                            : null,

                    storedProcedureId:
                        deleteRecord.recordType ===
                            "SP"
                            ? deleteRecord.storedProcedureId
                            : null,
                });

            const responseData =
                extractSingleResult(response);

            showNotification(
                responseData?.Message ||
                responseData?.message ||
                "Database record deleted successfully."
            );

            setDeleteRecord(null);
            await loadRecords();
        } catch (error) {
            showNotification(
                getErrorMessage(error),
                "error"
            );
        } finally {
            setDeleting(false);
        }
    };

    const groupedRecords = useMemo(() => {
        const groupMap = new Map();

        records.forEach((record) => {
            const normalizedSubModuleId =
                record.subModuleId === "" ||
                    record.subModuleId === null ||
                    record.subModuleId === undefined
                    ? null
                    : record.subModuleId;

            const scopeKey = [
                record.applicationId,
                record.moduleId,
                normalizedSubModuleId ??
                "MODULE_LEVEL",
            ].join("-");

            if (!groupMap.has(scopeKey)) {
                groupMap.set(scopeKey, {
                    scopeKey,
                    applicationId:
                        record.applicationId,
                    moduleId: record.moduleId,
                    subModuleId:
                        normalizedSubModuleId,
                    tableCount: 0,
                    storedProcedureCount: 0,
                    activeCount: 0,
                    inactiveCount: 0,
                    records: [],
                });
            }

            const group =
                groupMap.get(scopeKey);

            group.records.push(record);

            if (
                record.recordType === "TABLE"
            ) {
                group.tableCount += 1;
            } else if (
                record.recordType === "SP"
            ) {
                group.storedProcedureCount += 1;
            }

            if (record.isActive) {
                group.activeCount += 1;
            } else {
                group.inactiveCount += 1;
            }
        });

        return Array.from(
            groupMap.values()
        ).sort((first, second) => {
            const firstText = [
                getApplicationName(
                    first.applicationId
                ),
                getModuleName(first.moduleId),
                getSubModuleName(
                    first.subModuleId
                ),
            ]
                .join(" ")
                .toLowerCase();

            const secondText = [
                getApplicationName(
                    second.applicationId
                ),
                getModuleName(second.moduleId),
                getSubModuleName(
                    second.subModuleId
                ),
            ]
                .join(" ")
                .toLowerCase();

            return firstText.localeCompare(
                secondText
            );
        });
    }, [
        records,
        getApplicationName,
        getModuleName,
        getSubModuleName,
    ]);

    const filteredGroupedRecords =
        useMemo(() => {
            const globalSearchValue =
                searchText
                    .trim()
                    .toLowerCase();

            const includesFilter = (
                value,
                filterValue
            ) => {
                if (!filterValue) {
                    return true;
                }

                return String(value ?? "")
                    .toLowerCase()
                    .includes(
                        String(filterValue)
                            .trim()
                            .toLowerCase()
                    );
            };

            return groupedRecords.filter(
                (group) => {
                    if (
                        recordTypeFilter ===
                        "TABLE" &&
                        group.tableCount === 0
                    ) {
                        return false;
                    }

                    if (
                        recordTypeFilter === "SP" &&
                        group.storedProcedureCount ===
                        0
                    ) {
                        return false;
                    }

                    const applicationName =
                        getApplicationName(
                            group.applicationId
                        );

                    const moduleName =
                        getModuleName(
                            group.moduleId
                        );

                    const subModuleName =
                        getSubModuleName(
                            group.subModuleId
                        );

                    const statusText =
                        `Active ${group.activeCount} ` +
                        `Inactive ${group.inactiveCount}`;

                    const matchesColumnFilters =
                        includesFilter(
                            applicationName,
                            columnFilters.application
                        ) &&
                        includesFilter(
                            moduleName,
                            columnFilters.module
                        ) &&
                        includesFilter(
                            subModuleName,
                            columnFilters.subModule
                        ) &&
                        includesFilter(
                            group.tableCount,
                            columnFilters.tables
                        ) &&
                        includesFilter(
                            group.storedProcedureCount,
                            columnFilters.storedProcedures
                        );

                    if (!matchesColumnFilters) {
                        return false;
                    }

                    if (!globalSearchValue) {
                        return true;
                    }

                    const childRecordText =
                        group.records
                            .flatMap((record) => [
                                record.databaseName,
                                record.schemaName,
                                record.tableName,
                                record.tableDescription,
                                record.spName,
                                record.spDescription,
                            ])
                            .join(" ");

                    const searchableText = [
                        applicationName,
                        moduleName,
                        subModuleName,
                        group.tableCount,
                        group.storedProcedureCount,
                        statusText,
                        childRecordText,
                    ]
                        .join(" ")
                        .toLowerCase();

                    return searchableText.includes(
                        globalSearchValue
                    );
                }
            );
        }, [
            groupedRecords,
            searchText,
            recordTypeFilter,
            columnFilters,
            getApplicationName,
            getModuleName,
            getSubModuleName,
        ]);

    const sortedGroupedRecords =
        useMemo(() => {
            const sortedRecords = [
                ...filteredGroupedRecords,
            ];

            if (!orderBy) {
                return sortedRecords;
            }

            const getSortValue = (group) => {
                switch (orderBy) {
                    case "application":
                        return getApplicationName(
                            group.applicationId
                        ).toLowerCase();

                    case "module":
                        return getModuleName(
                            group.moduleId
                        ).toLowerCase();

                    case "subModule":
                        return getSubModuleName(
                            group.subModuleId
                        ).toLowerCase();

                    case "tables":
                        return group.tableCount;

                    case "storedProcedures":
                        return group.storedProcedureCount;

                    case "status":
                        return group.activeCount;

                    default:
                        return "";
                }
            };

            sortedRecords.sort(
                (first, second) => {
                    const firstValue =
                        getSortValue(first);

                    const secondValue =
                        getSortValue(second);

                    if (
                        typeof firstValue ===
                        "number" &&
                        typeof secondValue ===
                        "number"
                    ) {
                        return order === "asc"
                            ? firstValue - secondValue
                            : secondValue - firstValue;
                    }

                    return order === "asc"
                        ? String(firstValue).localeCompare(
                            String(secondValue)
                        )
                        : String(secondValue).localeCompare(
                            String(firstValue)
                        );
                }
            );

            return sortedRecords;
        }, [
            filteredGroupedRecords,
            order,
            orderBy,
            getApplicationName,
            getModuleName,
            getSubModuleName,
        ]);

    const paginatedGroupedRecords =
        useMemo(() => {
            const startIndex =
                page * rowsPerPage;

            return sortedGroupedRecords.slice(
                startIndex,
                startIndex +
                rowsPerPage
            );
        }, [
            sortedGroupedRecords,
            page,
            rowsPerPage,
        ]);

    useEffect(() => {
        setPage(0);
    }, [
        searchText,
        recordTypeFilter,
        columnFilters,
    ]);

    useEffect(() => {
        const lastPage = Math.max(
            0,
            Math.ceil(
                filteredGroupedRecords.length /
                rowsPerPage
            ) - 1
        );

        if (page > lastPage) {
            setPage(lastPage);
        }
    }, [
        filteredGroupedRecords.length,
        rowsPerPage,
        page,
    ]);

    const openViewDialog = (group) => {
        setViewScope({
            applicationId:
                group.applicationId,
            moduleId: group.moduleId,
            subModuleId:
                group.subModuleId,
        });
    };

    const closeViewDialog = () => {
        setViewScope(null);
    };

    const openAddFormForViewedScope = (recordType) => {
        if (!viewScope) {
            showNotification(
                "Unable to identify the selected module or submodule.",
                "error"
            );
            return;
        }

        const selectedSubModuleId =
            viewScope.subModuleId === "" ||
                viewScope.subModuleId === null ||
                viewScope.subModuleId === undefined
                ? null
                : String(viewScope.subModuleId);

        const scopeRecords = records.filter((record) => {
            const recordSubModuleId =
                record.subModuleId === "" ||
                    record.subModuleId === null ||
                    record.subModuleId === undefined
                    ? null
                    : String(record.subModuleId);

            return (
                String(record.applicationId) ===
                String(viewScope.applicationId) &&
                String(record.moduleId) ===
                String(viewScope.moduleId) &&
                recordSubModuleId === selectedSubModuleId
            );
        });

        const databaseNames = Array.from(
            new Set(
                scopeRecords
                    .map((record) =>
                        String(record.databaseName || "").trim()
                    )
                    .filter(Boolean)
            )
        );

        const normalizedRecordType =
            recordType === "SP" ? "SP" : "TABLE";

        setForm({
            ...createEmptyForm(),
            applicationId: viewScope.applicationId,
            moduleId: viewScope.moduleId,
            subModuleId: viewScope.subModuleId ?? "",
            databaseName:
                databaseNames.length === 1
                    ? databaseNames[0]
                    : "",
            tables:
                normalizedRecordType === "TABLE"
                    ? [createEmptyTable()]
                    : [],
            storedProcedures:
                normalizedRecordType === "SP"
                    ? [createEmptyStoredProcedure()]
                    : [],
        });

        setScopeAddContext({
            applicationId: viewScope.applicationId,
            moduleId: viewScope.moduleId,
            subModuleId: viewScope.subModuleId ?? null,
            recordType: normalizedRecordType,
        });

        setViewScope(null);
        setShowForm(true);

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const viewedScopeRecords =
        useMemo(() => {
            if (!viewScope) {
                return [];
            }

            return records.filter(
                (record) => {
                    const sameApplication =
                        String(
                            record.applicationId
                        ) ===
                        String(
                            viewScope.applicationId
                        );

                    const sameModule =
                        String(record.moduleId) ===
                        String(viewScope.moduleId);

                    const recordSubModule =
                        record.subModuleId === "" ||
                            record.subModuleId === null ||
                            record.subModuleId ===
                            undefined
                            ? null
                            : String(
                                record.subModuleId
                            );

                    const selectedSubModule =
                        viewScope.subModuleId === "" ||
                            viewScope.subModuleId === null ||
                            viewScope.subModuleId ===
                            undefined
                            ? null
                            : String(
                                viewScope.subModuleId
                            );

                    return (
                        sameApplication &&
                        sameModule &&
                        recordSubModule ===
                        selectedSubModule
                    );
                }
            );
        }, [
            records,
            viewScope,
        ]);

    const viewedTables = useMemo(
        () =>
            viewedScopeRecords.filter(
                (record) =>
                    record.recordType ===
                    "TABLE"
            ),
        [viewedScopeRecords]
    );

    const viewedStoredProcedures =
        useMemo(
            () =>
                viewedScopeRecords.filter(
                    (record) =>
                        record.recordType ===
                        "SP"
                ),
            [viewedScopeRecords]
        );

    return (
        <PageContainer
            title="Master Database Management"
            description="Manage database tables and stored procedures."
        >
            <Breadcrumb
                title="Master Database Management"
                items={BCrumb}
            />

            {/* ADD / EDIT CARD */}
            {showForm && (
                <Card
                    sx={{
                        mb: 2,
                        p: 0,
                        overflow: "hidden",
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        px={2}
                        py={1.5}
                        sx={{
                            backgroundColor:
                                "primary.light",
                            color: "primary.main",
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                        >
                            {isEditMode ? (
                                <EditIcon fontSize="small" />
                            ) : (
                                <AddIcon fontSize="small" />
                            )}

                            <Typography fontWeight={500}>
                                {isEditMode
                                    ? `Edit ${form.recordType ===
                                        "TABLE"
                                        ? "Table"
                                        : "Stored Procedure"
                                    }`
                                    : isScopeAddMode
                                        ? `Add New ${scopeAddContext?.recordType ===
                                            "TABLE"
                                            ? "Table"
                                            : "Stored Procedure"
                                        } to Existing Scope`
                                        : "Add Database Details"}
                            </Typography>
                        </Stack>

                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                        >
                            {isEditMode && (
                                <Chip
                                    size="small"
                                    label="Edit Mode"
                                    color="primary"
                                    variant="outlined"
                                />
                            )}

                            {isScopeAddMode && (
                                <Chip
                                    size="small"
                                    label="Existing Scope"
                                    color="success"
                                    variant="outlined"
                                />
                            )}

                            <Tooltip title="Close form">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={resetForm}
                                        disabled={saving}
                                        color="primary"
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    </Box>

                    <CardContent
                        sx={{
                            px: 3,
                            py: 2,
                        }}
                    >
                        {isScopeAddMode && (
                            <Alert
                                severity="info"
                                sx={{ mb: 2 }}
                            >
                                Adding a new database object to the selected
                                application, module, and submodule. The scope
                                fields are locked to prevent adding it to the
                                wrong location.
                            </Alert>
                        )}

                        <Grid
                            container
                            spacing={2}
                            alignItems="flex-start"
                        >
                            <Grid
                                item
                                xs={12}
                                md={6}
                                lg={3}
                            >
                                <Typography
                                    variant="caption"
                                    fontWeight={600}
                                >
                                    Application *
                                </Typography>

                                <FormControl
                                    fullWidth
                                    size="small"
                                    disabled={isScopeAddMode}
                                    sx={{ mt: 0.5 }}
                                >
                                    <InputLabel>
                                        Application
                                    </InputLabel>

                                    <Select
                                        label="Application"
                                        value={
                                            form.applicationId
                                        }
                                        onChange={
                                            handleApplicationChange
                                        }
                                    >
                                        <MenuItem value="">
                                            Select Application
                                        </MenuItem>

                                        {applications.map(
                                            (item) => (
                                                <MenuItem
                                                    key={item.id}
                                                    value={item.id}
                                                >
                                                    {item.name}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}
                                lg={3}
                            >
                                <Typography
                                    variant="caption"
                                    fontWeight={600}
                                >
                                    Module *
                                </Typography>

                                <FormControl
                                    fullWidth
                                    size="small"
                                    disabled={
                                        !form.applicationId ||
                                        isScopeAddMode
                                    }
                                    sx={{ mt: 0.5 }}
                                >
                                    <InputLabel>
                                        Module
                                    </InputLabel>

                                    <Select
                                        label="Module"
                                        value={form.moduleId}
                                        onChange={
                                            handleModuleChange
                                        }
                                    >
                                        <MenuItem value="">
                                            Select Module
                                        </MenuItem>

                                        {filteredModules.map(
                                            (item) => (
                                                <MenuItem
                                                    key={item.id}
                                                    value={item.id}
                                                >
                                                    {item.name}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}
                                lg={3}
                            >
                                <Typography
                                    variant="caption"
                                    fontWeight={600}
                                >
                                    Submodule
                                </Typography>

                                <FormControl
                                    fullWidth
                                    size="small"
                                    disabled={
                                        !form.moduleId ||
                                        isScopeAddMode
                                    }
                                    sx={{ mt: 0.5 }}
                                >
                                    <InputLabel>
                                        Submodule (Optional)
                                    </InputLabel>

                                    <Select
                                        name="subModuleId"
                                        label="Submodule (Optional)"
                                        value={
                                            form.subModuleId
                                        }
                                        onChange={
                                            handleFieldChange
                                        }
                                    >
                                        <MenuItem value="">
                                            No Submodule — Module
                                            Level
                                        </MenuItem>

                                        {filteredSubModules.map(
                                            (item) => (
                                                <MenuItem
                                                    key={item.id}
                                                    value={item.id}
                                                >
                                                    {item.name}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}
                                lg={3}
                            >
                                <Typography
                                    variant="caption"
                                    fontWeight={600}
                                >
                                    Database *
                                </Typography>

                                <FormControl
                                    fullWidth
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                >
                                    <InputLabel>
                                        Database
                                    </InputLabel>

                                    <Select
                                        name="databaseName"
                                        label="Database"
                                        value={
                                            form.databaseName
                                        }
                                        onChange={
                                            handleFieldChange
                                        }
                                    >
                                        <MenuItem value="">
                                            Select Database
                                        </MenuItem>

                                        {DATABASE_OPTIONS.map(
                                            (databaseName) => (
                                                <MenuItem
                                                    key={databaseName}
                                                    value={databaseName}
                                                >
                                                    {databaseName}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {isEditMode ? (
                            <Box
                                sx={{
                                    mt: 2.5,
                                    p: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                }}
                            >
                                <Alert
                                    severity="info"
                                    sx={{ mb: 2 }}
                                >
                                    Schema is saved separately
                                    for this individual{" "}
                                    {form.recordType === "TABLE"
                                        ? "table"
                                        : "stored procedure"}.
                                </Alert>

                                <Grid
                                    container
                                    spacing={1.5}
                                    alignItems="flex-start"
                                >
                                    <Grid
                                        item
                                        xs={12}
                                        md={3}
                                    >
                                        <FormControl
                                            fullWidth
                                            size="small"
                                        >
                                            <InputLabel>
                                                Schema
                                            </InputLabel>

                                            <Select
                                                label="Schema"
                                                value={
                                                    form.editSchemaOption
                                                }
                                                onChange={(event) =>
                                                    setForm(
                                                        (previous) => ({
                                                            ...previous,

                                                            editSchemaOption:
                                                                event.target
                                                                    .value,

                                                            editCustomSchemaName:
                                                                event.target
                                                                    .value ===
                                                                    "OTHER"
                                                                    ? previous.editCustomSchemaName
                                                                    : "",
                                                        })
                                                    )
                                                }
                                            >
                                                <MenuItem value="">
                                                    Select Schema
                                                </MenuItem>

                                                {SCHEMA_OPTIONS.map(
                                                    (schema) => (
                                                        <MenuItem
                                                            key={
                                                                schema.value
                                                            }
                                                            value={
                                                                schema.value
                                                            }
                                                        >
                                                            {schema.label}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {form.editSchemaOption ===
                                        "OTHER" && (
                                            <Grid
                                                item
                                                xs={12}
                                                md={3}
                                            >
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="editCustomSchemaName"
                                                    label="Custom Schema"
                                                    value={
                                                        form.editCustomSchemaName
                                                    }
                                                    onChange={
                                                        handleFieldChange
                                                    }
                                                    inputProps={{
                                                        maxLength: 128,
                                                    }}
                                                />
                                            </Grid>
                                        )}

                                    <Grid
                                        item
                                        xs={12}
                                        md={
                                            form.editSchemaOption ===
                                                "OTHER"
                                                ? 3
                                                : 4
                                        }
                                    >
                                        <TextField
                                            fullWidth
                                            size="small"
                                            name="editName"
                                            label={
                                                form.recordType ===
                                                    "TABLE"
                                                    ? "Table Name"
                                                    : "Stored Procedure Name"
                                            }
                                            value={form.editName}
                                            onChange={
                                                handleFieldChange
                                            }
                                            inputProps={{
                                                maxLength: 256,
                                            }}
                                        />
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                        md={
                                            form.editSchemaOption ===
                                                "OTHER"
                                                ? 3
                                                : 5
                                        }
                                    >
                                        <TextField
                                            fullWidth
                                            size="small"
                                            name="editDescription"
                                            label="Description"
                                            value={
                                                form.editDescription
                                            }
                                            onChange={
                                                handleFieldChange
                                            }
                                            multiline
                                            minRows={1}
                                            maxRows={3}
                                            inputProps={{
                                                maxLength: 1000,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={
                                                        form.isActive
                                                    }
                                                    onChange={(event) =>
                                                        setForm(
                                                            (previous) => ({
                                                                ...previous,
                                                                isActive:
                                                                    event.target
                                                                        .checked,
                                                            })
                                                        )
                                                    }
                                                />
                                            }
                                            label={
                                                form.isActive
                                                    ? "Active"
                                                    : "Inactive"
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        ) : (
                            <>
                                {/* TABLE ROWS */}
                                <Box
                                    sx={{
                                        mt: 2.5,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        px={2}
                                        py={1.25}
                                        sx={{
                                            backgroundColor:
                                                "#fafbfb",
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                color="primary.main"
                                                fontWeight={500}
                                            >
                                                Database Tables
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Select schema separately
                                                for every table.
                                            </Typography>
                                        </Box>

                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={addTableRow}
                                        >
                                            Add Table
                                        </Button>
                                    </Box>

                                    <Box
                                        sx={{
                                            p: 1,
                                            maxHeight: 320,
                                            overflowY: "auto",
                                        }}
                                    >
                                        {form.tables.map(
                                            (item, index) => (
                                                <Box
                                                    key={item.rowKey}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems:
                                                            "flex-start",
                                                        gap: 1,
                                                        py: 1,
                                                        borderBottom:
                                                            index <
                                                                form.tables.length -
                                                                1
                                                                ? "1px solid"
                                                                : "none",
                                                        borderColor:
                                                            "divider",
                                                    }}
                                                >
                                                    <FormControl
                                                        size="small"
                                                        sx={{
                                                            flex: "0 0 15%",
                                                            minWidth: 130,
                                                        }}
                                                    >
                                                        <InputLabel>
                                                            Schema
                                                        </InputLabel>

                                                        <Select
                                                            label="Schema"
                                                            value={
                                                                item.schemaOption
                                                            }
                                                            onChange={(
                                                                event
                                                            ) =>
                                                                handleTableSchemaChange(
                                                                    item.rowKey,
                                                                    event.target
                                                                        .value
                                                                )
                                                            }
                                                        >
                                                            <MenuItem value="">
                                                                Select Schema
                                                            </MenuItem>

                                                            {SCHEMA_OPTIONS.map(
                                                                (schema) => (
                                                                    <MenuItem
                                                                        key={
                                                                            schema.value
                                                                        }
                                                                        value={
                                                                            schema.value
                                                                        }
                                                                    >
                                                                        {
                                                                            schema.label
                                                                        }
                                                                    </MenuItem>
                                                                )
                                                            )}
                                                        </Select>
                                                    </FormControl>

                                                    {item.schemaOption ===
                                                        "OTHER" && (
                                                            <TextField
                                                                size="small"
                                                                label="Custom Schema"
                                                                value={
                                                                    item.customSchemaName
                                                                }
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    handleTableChange(
                                                                        item.rowKey,
                                                                        "customSchemaName",
                                                                        event.target
                                                                            .value
                                                                    )
                                                                }
                                                                sx={{
                                                                    flex: "0 0 15%",
                                                                    minWidth: 130,
                                                                }}
                                                                inputProps={{
                                                                    maxLength: 128,
                                                                }}
                                                            />
                                                        )}

                                                    <TextField
                                                        size="small"
                                                        label="Table Name"
                                                        value={
                                                            item.tableName
                                                        }
                                                        onChange={(event) =>
                                                            handleTableChange(
                                                                item.rowKey,
                                                                "tableName",
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                        sx={{
                                                            flex:
                                                                item.schemaOption ===
                                                                    "OTHER"
                                                                    ? "0 0 18%"
                                                                    : "0 0 24%",
                                                            minWidth: 170,
                                                        }}
                                                        inputProps={{
                                                            maxLength: 256,
                                                        }}
                                                    />

                                                    <TextField
                                                        size="small"
                                                        label="Table Description"
                                                        value={
                                                            item.tableDescription
                                                        }
                                                        onChange={(event) =>
                                                            handleTableChange(
                                                                item.rowKey,
                                                                "tableDescription",
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                        sx={{ flex: 1 }}
                                                        multiline
                                                        minRows={1}
                                                        maxRows={3}
                                                        inputProps={{
                                                            maxLength: 1000,
                                                        }}
                                                    />

                                                    <FormControlLabel
                                                        sx={{
                                                            m: 0,
                                                            minWidth: 95,
                                                        }}
                                                        control={
                                                            <Switch
                                                                size="small"
                                                                checked={
                                                                    item.isActive !==
                                                                    false
                                                                }
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    handleTableChange(
                                                                        item.rowKey,
                                                                        "isActive",
                                                                        event.target
                                                                            .checked
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label={
                                                            item.isActive !== false
                                                                ? "Active"
                                                                : "Inactive"
                                                        }
                                                    />

                                                    <Tooltip title="Remove table">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() =>
                                                                removeTableRow(
                                                                    item.rowKey
                                                                )
                                                            }
                                                            sx={{ mt: 0.5 }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            )
                                        )}
                                    </Box>
                                </Box>

                                {/* STORED PROCEDURE ROWS */}
                                <Box
                                    sx={{
                                        mt: 2,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        px={2}
                                        py={1.25}
                                        sx={{
                                            backgroundColor:
                                                "#fafbfb",
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                color="secondary.main"
                                                fontWeight={500}
                                            >
                                                Stored Procedures
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Select schema separately
                                                for every stored
                                                procedure.
                                            </Typography>
                                        </Box>

                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={
                                                addStoredProcedureRow
                                            }
                                        >
                                            Add SP
                                        </Button>
                                    </Box>

                                    <Box
                                        sx={{
                                            p: 1,
                                            maxHeight: 320,
                                            overflowY: "auto",
                                        }}
                                    >
                                        {form.storedProcedures.map(
                                            (item, index) => (
                                                <Box
                                                    key={item.rowKey}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems:
                                                            "flex-start",
                                                        gap: 1,
                                                        py: 1,
                                                        borderBottom:
                                                            index <
                                                                form
                                                                    .storedProcedures
                                                                    .length -
                                                                1
                                                                ? "1px solid"
                                                                : "none",
                                                        borderColor:
                                                            "divider",
                                                    }}
                                                >
                                                    <FormControl
                                                        size="small"
                                                        sx={{
                                                            flex: "0 0 15%",
                                                            minWidth: 130,
                                                        }}
                                                    >
                                                        <InputLabel>
                                                            Schema
                                                        </InputLabel>

                                                        <Select
                                                            label="Schema"
                                                            value={
                                                                item.schemaOption
                                                            }
                                                            onChange={(
                                                                event
                                                            ) =>
                                                                handleStoredProcedureSchemaChange(
                                                                    item.rowKey,
                                                                    event.target
                                                                        .value
                                                                )
                                                            }
                                                        >
                                                            <MenuItem value="">
                                                                Select Schema
                                                            </MenuItem>

                                                            {SCHEMA_OPTIONS.map(
                                                                (schema) => (
                                                                    <MenuItem
                                                                        key={
                                                                            schema.value
                                                                        }
                                                                        value={
                                                                            schema.value
                                                                        }
                                                                    >
                                                                        {
                                                                            schema.label
                                                                        }
                                                                    </MenuItem>
                                                                )
                                                            )}
                                                        </Select>
                                                    </FormControl>

                                                    {item.schemaOption ===
                                                        "OTHER" && (
                                                            <TextField
                                                                size="small"
                                                                label="Custom Schema"
                                                                value={
                                                                    item.customSchemaName
                                                                }
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    handleStoredProcedureChange(
                                                                        item.rowKey,
                                                                        "customSchemaName",
                                                                        event.target
                                                                            .value
                                                                    )
                                                                }
                                                                sx={{
                                                                    flex: "0 0 15%",
                                                                    minWidth: 130,
                                                                }}
                                                                inputProps={{
                                                                    maxLength: 128,
                                                                }}
                                                            />
                                                        )}

                                                    <TextField
                                                        size="small"
                                                        label="Stored Procedure Name"
                                                        value={item.spName}
                                                        onChange={(event) =>
                                                            handleStoredProcedureChange(
                                                                item.rowKey,
                                                                "spName",
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                        sx={{
                                                            flex:
                                                                item.schemaOption ===
                                                                    "OTHER"
                                                                    ? "0 0 18%"
                                                                    : "0 0 24%",
                                                            minWidth: 190,
                                                        }}
                                                        inputProps={{
                                                            maxLength: 256,
                                                        }}
                                                    />

                                                    <TextField
                                                        size="small"
                                                        label="Stored Procedure Description"
                                                        value={
                                                            item.spDescription
                                                        }
                                                        onChange={(event) =>
                                                            handleStoredProcedureChange(
                                                                item.rowKey,
                                                                "spDescription",
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                        sx={{ flex: 1 }}
                                                        multiline
                                                        minRows={1}
                                                        maxRows={3}
                                                        inputProps={{
                                                            maxLength: 1000,
                                                        }}
                                                    />

                                                    <FormControlLabel
                                                        sx={{
                                                            m: 0,
                                                            minWidth: 95,
                                                        }}
                                                        control={
                                                            <Switch
                                                                size="small"
                                                                checked={
                                                                    item.isActive !==
                                                                    false
                                                                }
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    handleStoredProcedureChange(
                                                                        item.rowKey,
                                                                        "isActive",
                                                                        event.target
                                                                            .checked
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label={
                                                            item.isActive !== false
                                                                ? "Active"
                                                                : "Inactive"
                                                        }
                                                    />

                                                    <Tooltip title="Remove stored procedure">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() =>
                                                                removeStoredProcedureRow(
                                                                    item.rowKey
                                                                )
                                                            }
                                                            sx={{ mt: 0.5 }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            )
                                        )}
                                    </Box>
                                </Box>
                            </>
                        )}

                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ mt: 2.5 }}
                        >
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={saving}
                                startIcon={
                                    saving ? (
                                        <CircularProgress
                                            size={17}
                                            color="inherit"
                                        />
                                    ) : (
                                        <SaveIcon />
                                    )
                                }
                            >
                                {saving
                                    ? "Saving..."
                                    : isEditMode
                                        ? "Update"
                                        : isScopeAddMode
                                            ? `Save ${scopeAddContext?.recordType ===
                                                "TABLE"
                                                ? "Table"
                                                : "Stored Procedure"
                                            }`
                                            : "Save All"}
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={resetForm}
                                disabled={saving}
                            >
                                {isEditMode || isScopeAddMode
                                    ? "Cancel"
                                    : "Reset"}
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            )}

            {/* EXISTING RECORDS CARD */}
            <Card
                sx={{
                    mt: 2,
                    p: 0,
                    overflow: "hidden",
                }}
            >
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1.5}
                    px={2}
                    py={1.5}
                    sx={{
                        backgroundColor:
                            "primary.light",
                        color: "primary.main",
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                    >
                        <VisibilityIcon fontSize="small" />

                        <Box sx={{ ml: 1 }}>
                            Existing Database Details
                        </Box>
                    </Box>

                    <Box
                        display="flex"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                    >
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={openAddForm}
                            sx={{ height: 32 }}
                        >
                            Add Detail
                        </Button>

                        <TextField
                            size="small"
                            placeholder="Search..."
                            value={searchText}
                            onFocus={() => {
                                setShowAllFilters(false);
                                setColumnFilters(
                                    createEmptyColumnFilters()
                                );
                            }}
                            onChange={(event) => {
                                setSearchText(
                                    event.target.value
                                );
                                setPage(0);
                            }}
                            sx={{
                                width: 220,
                                backgroundColor: "white",
                                borderRadius: 1,
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment
                                        position="start"
                                        sx={{ mr: 1 }}
                                    >
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {searchText && (
                            <CloseIcon
                                sx={{
                                    cursor: "pointer",
                                }}
                                onClick={() => {
                                    setSearchText("");
                                    setPage(0);
                                }}
                            />
                        )}

                        <Tooltip title="Column Filters">
                            <FilterAltIcon
                                sx={{
                                    cursor: "pointer",
                                    color: showAllFilters
                                        ? "primary.main"
                                        : "text.primary",
                                    transition: "0.2s",
                                }}
                                onClick={() => {
                                    setShowAllFilters(
                                        (previous) =>
                                            !previous
                                    );
                                    setSearchText("");
                                    setPage(0);
                                }}
                            />
                        </Tooltip>

                        <Button
                            variant="outlined"
                            size="small"
                            sx={{
                                height: 32,
                                backgroundColor: "white",
                            }}
                            onClick={handleResetFilters}
                        >
                            Reset Filter
                        </Button>
                    </Box>
                </Box>

                <CardContent>
                    <TableContainer
                        component={Paper}
                    >
                        <Table>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor:
                                            "#FAFBFB",
                                    }}
                                >
                                    <TableCell
                                        width={60}
                                        sx={{ fontWeight: 600 }}
                                    >
                                        #
                                    </TableCell>

                                    {renderSortableHeader(
                                        "Application",
                                        "application"
                                    )}

                                    {renderSortableHeader(
                                        "Module",
                                        "module"
                                    )}

                                    {renderSortableHeader(
                                        "Submodule / Scope",
                                        "subModule"
                                    )}

                                    {renderSortableHeader(
                                        "Tables",
                                        "tables",
                                        "center"
                                    )}

                                    {renderSortableHeader(
                                        "Stored Procedures",
                                        "storedProcedures",
                                        "center"
                                    )}

                                    {renderSortableHeader(
                                        "Status Summary",
                                        "status",
                                        "center"
                                    )}

                                    <TableCell
                                        align="center"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        Action
                                    </TableCell>
                                </TableRow>

                                {showAllFilters && (
                                    <TableRow>
                                        <TableCell />

                                        {renderColumnFilter(
                                            "application",
                                            "Search..."
                                        )}

                                        {renderColumnFilter(
                                            "module",
                                            "Search..."
                                        )}

                                        {renderColumnFilter(
                                            "subModule",
                                            "Search..."
                                        )}

                                        {renderColumnFilter(
                                            "tables",
                                            "Count...",
                                            "center"
                                        )}

                                        {renderColumnFilter(
                                            "storedProcedures",
                                            "Count...",
                                            "center"
                                        )}

                                        {/* No filter for Status Summary */}
                                        <TableCell />

                                        {/* Action column */}
                                        <TableCell />
                                    </TableRow>
                                )}
                            </TableHead>

                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={8}
                                            align="center"
                                            sx={{ py: 6 }}
                                        >
                                            <CircularProgress
                                                size={30}
                                            />

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mt: 1 }}
                                            >
                                                Loading database
                                                details...
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : paginatedGroupedRecords
                                    .length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={8}
                                            align="center"
                                            sx={{ py: 4 }}
                                        >
                                            No data found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedGroupedRecords.map(
                                        (group, index) => (
                                            <TableRow
                                                key={group.scopeKey}
                                                hover
                                            >
                                                <TableCell>
                                                    {page *
                                                        rowsPerPage +
                                                        index +
                                                        1}
                                                </TableCell>

                                                <TableCell>
                                                    {getApplicationName(
                                                        group.applicationId
                                                    )}
                                                </TableCell>

                                                <TableCell>
                                                    {getModuleName(
                                                        group.moduleId
                                                    )}
                                                </TableCell>

                                                <TableCell>
                                                    {getSubModuleName(
                                                        group.subModuleId
                                                    )}
                                                </TableCell>

                                                <TableCell align="center">
                                                    {group.tableCount}
                                                </TableCell>

                                                <TableCell align="center">
                                                    {
                                                        group.storedProcedureCount
                                                    }
                                                </TableCell>

                                                <TableCell align="center">
                                                    <Box
                                                        display="flex"
                                                        justifyContent="center"
                                                        alignItems="center"
                                                        gap={1}
                                                        flexWrap="wrap"
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            color="success.main"
                                                        >
                                                            Active:{" "}
                                                            {group.activeCount}
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Inactive:{" "}
                                                            {
                                                                group.inactiveCount
                                                            }
                                                        </Typography>
                                                    </Box>
                                                </TableCell>

                                                <TableCell align="center">
                                                    <Tooltip title="View all tables and stored procedures">
                                                        <VisibilityIcon
                                                            fontSize="small"
                                                            sx={{
                                                                cursor: "pointer",
                                                                color:
                                                                    "primary.main",
                                                            }}
                                                            onClick={() =>
                                                                openViewDialog(
                                                                    group
                                                                )
                                                            }
                                                        />
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[
                            5,
                            10,
                            25,
                            50,
                        ]}
                        component="div"
                        count={
                            filteredGroupedRecords.length
                        }
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(
                            event,
                            newPage
                        ) => setPage(newPage)}
                        onRowsPerPageChange={(
                            event
                        ) => {
                            setRowsPerPage(
                                parseInt(
                                    event.target.value,
                                    10
                                )
                            );

                            setPage(0);
                        }}
                        showFirstButton
                        showLastButton
                    />
                </CardContent>
            </Card>

            {/* VIEW DIALOG */}
            <Dialog
                open={Boolean(viewScope)}
                onClose={closeViewDialog}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle sx={{ p: 0 }}>
                    <Box
                        px={2.5}
                        py={1.5}
                        sx={{
                            backgroundColor:
                                "primary.light",
                            color: "primary.main",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Box>
                                <Typography
                                    variant="h6"
                                    color="primary.main"
                                >
                                    Database Object Details
                                </Typography>

                                {viewScope && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {getApplicationName(
                                            viewScope.applicationId
                                        )}
                                        {" / "}
                                        {getModuleName(
                                            viewScope.moduleId
                                        )}
                                        {" / "}
                                        {getSubModuleName(
                                            viewScope.subModuleId
                                        )}
                                    </Typography>
                                )}
                            </Box>

                            <IconButton
                                size="small"
                                onClick={closeViewDialog}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                    </Box>
                </DialogTitle>

                <DialogContent
                    sx={{
                        pt: "24px !important",
                        maxHeight: "75vh",
                        overflowY: "auto",
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            mb: 3,
                            flexWrap: "wrap",
                            gap: 1,
                        }}
                    >
                        <Chip
                            label={`Tables: ${viewedTables.length}`}
                            color="primary"
                            variant="outlined"
                        />

                        <Chip
                            label={`Stored Procedures: ${viewedStoredProcedures.length}`}
                            color="secondary"
                            variant="outlined"
                        />

                        <Chip
                            label={
                                viewScope?.subModuleId ===
                                    null ||
                                    viewScope?.subModuleId ===
                                    undefined ||
                                    viewScope?.subModuleId ===
                                    ""
                                    ? "Module Level"
                                    : "Submodule Level"
                            }
                            color="warning"
                            variant="outlined"
                        />

                        <Box sx={{ flexGrow: 1 }} />

                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() =>
                                openAddFormForViewedScope(
                                    "TABLE"
                                )
                            }
                        >
                            Add New Table
                        </Button>

                        <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            startIcon={<AddIcon />}
                            onClick={() =>
                                openAddFormForViewedScope(
                                    "SP"
                                )
                            }
                        >
                            Add New SP
                        </Button>
                    </Stack>

                    <Typography
                        variant="h6"
                        color="primary.main"
                        sx={{ mb: 1 }}
                    >
                        Database Tables
                    </Typography>

                    <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{ mb: 3 }}
                    >
                        <Table size="small">
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor:
                                            "#fafbfb",
                                    }}
                                >
                                    <TableCell>#</TableCell>
                                    <TableCell>
                                        Database
                                    </TableCell>
                                    <TableCell>
                                        Schema
                                    </TableCell>
                                    <TableCell>
                                        Table Name
                                    </TableCell>
                                    <TableCell>
                                        Description
                                    </TableCell>
                                    <TableCell align="center">
                                        Status
                                    </TableCell>
                                    <TableCell align="center">
                                        Action
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {viewedTables.length ===
                                    0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={7}
                                            align="center"
                                            sx={{ py: 3 }}
                                        >
                                            No tables found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    viewedTables.map(
                                        (record, index) => (
                                            <TableRow
                                                key={
                                                    record.clientKey
                                                }
                                                hover
                                            >
                                                <TableCell>
                                                    {index + 1}
                                                </TableCell>

                                                <TableCell>
                                                    {record.databaseName ||
                                                        "-"}
                                                </TableCell>

                                                <TableCell>
                                                    {record.schemaName ||
                                                        "-"}
                                                </TableCell>

                                                <TableCell>
                                                    {record.tableName ||
                                                        "-"}
                                                </TableCell>

                                                <TableCell>
                                                    {record.tableDescription ||
                                                        "-"}
                                                </TableCell>

                                                <TableCell align="center">
                                                    {statusUpdatingKey ===
                                                        record.clientKey ? (
                                                        <CircularProgress
                                                            size={20}
                                                        />
                                                    ) : (
                                                        <FormControlLabel
                                                            sx={{ m: 0 }}
                                                            control={
                                                                <Switch
                                                                    size="small"
                                                                    checked={
                                                                        record.isActive
                                                                    }
                                                                    onChange={(
                                                                        event
                                                                    ) =>
                                                                        handleRecordStatusChange(
                                                                            record,
                                                                            event
                                                                                .target
                                                                                .checked
                                                                        )
                                                                    }
                                                                />
                                                            }
                                                            label={
                                                                record.isActive
                                                                    ? "Active"
                                                                    : "Inactive"
                                                            }
                                                        />
                                                    )}
                                                </TableCell>

                                                <TableCell align="center">
                                                    <Tooltip title="Edit table">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() =>
                                                                openEditForm(
                                                                    record
                                                                )
                                                            }
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title="Delete table">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() =>
                                                                openDeleteDialog(
                                                                    record
                                                                )
                                                            }
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography
                        variant="h6"
                        color="secondary.main"
                        sx={{ mb: 1 }}
                    >
                        Stored Procedures
                    </Typography>

                    <TableContainer
                        component={Paper}
                        variant="outlined"
                    >
                        <Table size="small">
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor:
                                            "#fafbfb",
                                    }}
                                >
                                    <TableCell>#</TableCell>
                                    <TableCell>
                                        Database
                                    </TableCell>
                                    <TableCell>
                                        Schema
                                    </TableCell>
                                    <TableCell>
                                        Stored Procedure
                                    </TableCell>
                                    <TableCell>
                                        Description
                                    </TableCell>
                                    <TableCell align="center">
                                        Status
                                    </TableCell>
                                    <TableCell align="center">
                                        Action
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {viewedStoredProcedures.length ===
                                    0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={7}
                                            align="center"
                                            sx={{ py: 3 }}
                                        >
                                            No stored procedures
                                            found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    viewedStoredProcedures.map(
                                        (record, index) => (
                                            <TableRow
                                                key={
                                                    record.clientKey
                                                }
                                                hover
                                            >
                                                <TableCell>
                                                    {index + 1}
                                                </TableCell>

                                                <TableCell>
                                                    {record.databaseName ||
                                                        "-"}
                                                </TableCell>

                                                <TableCell>
                                                    {record.schemaName ||
                                                        "-"}
                                                </TableCell>

                                                <TableCell>
                                                    {record.spName ||
                                                        "-"}
                                                </TableCell>

                                                <TableCell>
                                                    {record.spDescription ||
                                                        "-"}
                                                </TableCell>

                                                <TableCell align="center">
                                                    {statusUpdatingKey ===
                                                        record.clientKey ? (
                                                        <CircularProgress
                                                            size={20}
                                                        />
                                                    ) : (
                                                        <FormControlLabel
                                                            sx={{ m: 0 }}
                                                            control={
                                                                <Switch
                                                                    size="small"
                                                                    checked={
                                                                        record.isActive
                                                                    }
                                                                    onChange={(
                                                                        event
                                                                    ) =>
                                                                        handleRecordStatusChange(
                                                                            record,
                                                                            event
                                                                                .target
                                                                                .checked
                                                                        )
                                                                    }
                                                                />
                                                            }
                                                            label={
                                                                record.isActive
                                                                    ? "Active"
                                                                    : "Inactive"
                                                            }
                                                        />
                                                    )}
                                                </TableCell>

                                                <TableCell align="center">
                                                    <Tooltip title="Edit stored procedure">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() =>
                                                                openEditForm(
                                                                    record
                                                                )
                                                            }
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title="Delete stored procedure">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() =>
                                                                openDeleteDialog(
                                                                    record
                                                                )
                                                            }
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>

                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={closeViewDialog}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* DELETE DIALOG */}
            <Dialog
                open={Boolean(deleteRecord)}
                onClose={closeDeleteDialog}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>
                    Delete Database Detail
                </DialogTitle>

                <DialogContent dividers>
                    <Alert severity="warning">
                        This action performs a soft
                        delete. Are you sure you want
                        to delete this{" "}
                        {deleteRecord?.recordType ===
                            "TABLE"
                            ? "table"
                            : "stored procedure"}{" "}
                        record?
                    </Alert>

                    <Typography
                        variant="body2"
                        sx={{ mt: 2 }}
                    >
                        <strong>Name:</strong>{" "}
                        {deleteRecord
                            ? `${deleteRecord.schemaName ||
                            ""
                            }.${getRecordName(
                                deleteRecord
                            ) || ""
                            }`
                            : "-"}
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={closeDeleteDialog}
                        disabled={deleting}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        disabled={deleting}
                        startIcon={
                            deleting ? (
                                <CircularProgress
                                    size={17}
                                    color="inherit"
                                />
                            ) : (
                                <DeleteIcon />
                            )
                        }
                    >
                        {deleting
                            ? "Deleting..."
                            : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={notification.open}
                autoHideDuration={4500}
                onClose={closeNotification}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert
                    onClose={closeNotification}
                    severity={
                        notification.severity
                    }
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </PageContainer>
    );
};

export default MasterDatabase;
